import { useEffect, useState } from 'react';
import { authenticatedFetch } from '../utils/api';

interface IndexFuzzinessConfig {
  indexName: string;
  displayName: string;
  fuzziness: string; // 'AUTO', 'AUTO:5,8', '0', '1', '2', or custom
  enabled: boolean;
}

interface FuzzinessConfig {
  defaultFuzziness: string;
  indices: IndexFuzzinessConfig[];
  lastUpdated: string;
}

interface FuzzinessSettingsProps {
  onSave?: (config: FuzzinessConfig) => Promise<void>;
  onReset?: () => void;
  saving?: boolean;
}

const AVAILABLE_INDICES = [
  // ASM Info and Products
  { name: 'asmi-wp-pages', displayName: 'Member & Society Info' },
  { name: 'asmi-wp-products,asmi-marketing-gear-store', displayName: 'Products' },
  { name: 'asmi-wp-events', displayName: 'Education Courses' },
  { name: 'asmi-pages-events', displayName: 'Event Pages' },
  { name: 'asmi-wp-asm-news', displayName: 'Industry News' },
  { name: 'asmi-wp-videos', displayName: 'Videos' },
  // Digital Book Content
  { name: 'asmi-dl-books', displayName: 'Digital Books' },
  { name: 'asmi-dl-chapters', displayName: 'Digital Book Chapters' },
  { name: 'asmi-dl-chapter-images', displayName: 'Digital Book Images' },
  { name: 'asmi-dl-chapter-tables', displayName: 'Digital Book Tables' },
  // Other Digital Library Content
  { name: 'asmi-dl-technical_articles', displayName: 'Magazine Articles' },
  { name: 'asmi-wp-news_articles', displayName: 'ASM Member News' },
  { name: 'asmi-dl-proceedings', displayName: 'Conference Papers' },
  { name: 'asmi-dl-datasheets', displayName: 'Alloy Digest Datasheets' },
  // Materials Data indexes
  { name: 'asmi-mat-matdb-materials', displayName: 'Materials Database' },
  { name: 'asmi-mat-alloyfinder-alloy-finder', displayName: 'Alloy Finder' },
  { name: 'asmi-mat-datasheets-data-sheets-and-diagrams', displayName: 'Materials Datasheets' },
  { name: 'asmi-mat-grantami_mgd-micrograph-data', displayName: 'Micrograph Data' },
  { name: 'asmi-mat-corrosion-uniform-corrosion-data', displayName: 'Corrosion Data' },
  { name: 'asmi-mat-corrosion-environments', displayName: 'Corrosion Environments' },
  { name: 'asmi-mat-matdb-documents', displayName: 'Materials Documents' },
  { name: 'asmi-mat-datasheets-data-sheet-sources', displayName: 'Materials Data Sources' },
  { name: 'asmi-mat-matdb-reference', displayName: 'Materials References' },
  { name: 'asmi-mat-corrosion-design-data', displayName: 'Corrosion Design Data' },
  { name: 'asmi-mat-corrosion-references', displayName: 'Corrosion References' },
  { name: 'asmi-mat-alloyfinder-organizations', displayName: 'Alloy Organizations' },
  { name: 'asmi-mat-corrosion-materials', displayName: 'Corrosion Materials' },
  { name: 'asmi-mat-corrosion-high-temperature-oxidation-data', displayName: 'High-Temperature Oxidation Data' },
  { name: 'asmi-mat-alloy-phase-diagrams', displayName: 'Phase Diagrams' },
  { name: 'asmi-mat-alloy-phase-structures', displayName: 'Crystallographic Structures' },
  { name: 'asmi-mat-medical-materials', displayName: 'Medical Materials Database' },
];

// Preset fuzziness options with descriptions
const FUZZINESS_PRESETS = [
  { value: 'AUTO', label: 'AUTO (Default)', description: '0 edits for 1-2 chars, 1 edit for 3-5 chars, 2 edits for 6+ chars' },
  { value: 'AUTO:5,8', label: 'Strict', description: 'No edits for 1-4 chars, 1 edit for 5-7 chars, 2 edits for 8+ chars' },
  { value: 'AUTO:4,7', label: 'Moderate', description: 'No edits for 1-3 chars, 1 edit for 4-6 chars, 2 edits for 7+ chars' },
  { value: '0', label: 'Exact Match Only', description: 'No fuzzy matching - terms must match exactly' },
  { value: '1', label: '1 Edit Always', description: 'Allow 1 character edit for all terms' },
  { value: '2', label: '2 Edits Always', description: 'Allow up to 2 character edits for all terms' },
];

const DEFAULT_CONFIG: FuzzinessConfig = {
  defaultFuzziness: 'AUTO',
  indices: AVAILABLE_INDICES.map((idx) => ({
    indexName: idx.name,
    displayName: idx.displayName,
    fuzziness: 'AUTO',
    enabled: false, // By default, use the global setting
  })),
  lastUpdated: new Date().toISOString(),
};

export function FuzzinessSettings({ onSave, onReset, saving = false }: FuzzinessSettingsProps) {
  const [config, setConfig] = useState<FuzzinessConfig>(DEFAULT_CONFIG);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState<string | null>(null);
  const [showCustomInput, setShowCustomInput] = useState<{ [key: string]: boolean }>({});

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/fuzziness-settings`);
      if (response.ok) {
        const data = await response.json();
        // Merge with available indices to ensure all indices are present
        const mergedIndices = AVAILABLE_INDICES.map((idx) => {
          const existing = data.indices?.find((i: IndexFuzzinessConfig) => i.indexName === idx.name);
          return existing || {
            indexName: idx.name,
            displayName: idx.displayName,
            fuzziness: 'AUTO',
            enabled: false,
          };
        });
        setConfig({
          ...data,
          indices: mergedIndices,
        });
      }
    } catch (error) {
      console.error('Failed to fetch fuzziness settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDefaultFuzzinessChange = (fuzziness: string) => {
    setConfig((prev) => ({
      ...prev,
      defaultFuzziness: fuzziness,
      lastUpdated: new Date().toISOString(),
    }));
    setHasChanges(true);
  };

  const handleIndexFuzzinessChange = (indexName: string, fuzziness: string) => {
    setConfig((prev) => ({
      ...prev,
      indices: prev.indices.map((idx) =>
        idx.indexName === indexName ? { ...idx, fuzziness } : idx
      ),
      lastUpdated: new Date().toISOString(),
    }));
    setHasChanges(true);
  };

  const handleIndexEnabledChange = (indexName: string, enabled: boolean) => {
    setConfig((prev) => ({
      ...prev,
      indices: prev.indices.map((idx) =>
        idx.indexName === indexName ? { ...idx, enabled } : idx
      ),
      lastUpdated: new Date().toISOString(),
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(config);
      setHasChanges(false);
    } else {
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/fuzziness-settings`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(config),
        });
        if (response.ok) {
          setHasChanges(false);
        }
      } catch (error) {
        console.error('Failed to save fuzziness settings:', error);
      }
    }
  };

  const handleReset = async () => {
    if (onReset) {
      onReset();
    } else {
      if (confirm('Are you sure you want to reset all fuzziness settings to defaults?')) {
        try {
          const response = await authenticatedFetch(`${API_BASE_URL}/api/fuzziness-settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(DEFAULT_CONFIG),
          });
          if (response.ok) {
            setConfig(DEFAULT_CONFIG);
            setHasChanges(false);
          }
        } catch (error) {
          console.error('Failed to reset fuzziness settings:', error);
        }
      }
    }
  };

  const getFuzzinessLabel = (fuzziness: string) => {
    const preset = FUZZINESS_PRESETS.find((p) => p.value === fuzziness);
    return preset ? preset.label : `Custom: ${fuzziness}`;
  };

  const isCustomFuzziness = (fuzziness: string) => {
    return !FUZZINESS_PRESETS.some((p) => p.value === fuzziness);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-zinc-600">Loading fuzziness settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with explanation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">About Fuzzy Search</h3>
        <p className="text-sm text-blue-800">
          Fuzzy search allows finding results even when the search term has minor typos or spelling variations.
          The "fuzziness" setting controls how many character edits (insertions, deletions, substitutions) are allowed
          when matching terms.
        </p>
        <p className="text-sm text-blue-800 mt-2">
          <strong>Example:</strong> With <code className="bg-blue-100 px-1 rounded">AUTO</code> fuzziness, searching for "Cole"
          might also match "Cold" (1 character difference). Using <code className="bg-blue-100 px-1 rounded">AUTO:5,8</code>
          (Strict) prevents this for short words.
        </p>
      </div>

      {/* Global Default Setting */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-zinc-900 mb-4">Global Default Fuzziness</h3>
        <p className="text-sm text-zinc-600 mb-4">
          This setting applies to all indexes that don't have a custom fuzziness configuration.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {FUZZINESS_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => handleDefaultFuzzinessChange(preset.value)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                config.defaultFuzziness === preset.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-zinc-200 hover:border-zinc-300'
              }`}
            >
              <div className="font-medium text-zinc-900">{preset.label}</div>
              <div className="text-xs text-zinc-500 mt-1">{preset.description}</div>
            </button>
          ))}
        </div>

        {/* Custom input for global */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowCustomInput((prev) => ({ ...prev, global: !prev.global }))}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            {showCustomInput.global ? 'Hide custom input' : 'Enter custom value...'}
          </button>
          {showCustomInput.global && (
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                placeholder="e.g., AUTO:3,6"
                value={isCustomFuzziness(config.defaultFuzziness) ? config.defaultFuzziness : ''}
                onChange={(e) => handleDefaultFuzzinessChange(e.target.value)}
                className="flex-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Per-Index Settings */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-zinc-900 mb-2">Per-Index Overrides</h3>
        <p className="text-sm text-zinc-600 mb-4">
          Enable custom fuzziness for specific indexes to override the global default.
        </p>

        <div className="space-y-2">
          {config.indices.map((indexConfig) => (
            <div
              key={indexConfig.indexName}
              className={`border rounded-lg transition-all ${
                indexConfig.enabled ? 'border-blue-300 bg-blue-50/50' : 'border-zinc-200'
              }`}
            >
              {/* Index Header */}
              <div
                className="flex items-center justify-between p-3 cursor-pointer"
                onClick={() => setExpandedIndex(expandedIndex === indexConfig.indexName ? null : indexConfig.indexName)}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={indexConfig.enabled}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleIndexEnabledChange(indexConfig.indexName, e.target.checked);
                    }}
                    className="w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="font-medium text-zinc-900">{indexConfig.displayName}</div>
                    <div className="text-xs text-zinc-500">{indexConfig.indexName}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm px-2 py-1 rounded ${
                    indexConfig.enabled ? 'bg-blue-100 text-blue-700' : 'bg-zinc-100 text-zinc-500'
                  }`}>
                    {indexConfig.enabled ? getFuzzinessLabel(indexConfig.fuzziness) : 'Using global default'}
                  </span>
                  <svg
                    className={`w-5 h-5 text-zinc-400 transition-transform ${
                      expandedIndex === indexConfig.indexName ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Expanded Settings */}
              {expandedIndex === indexConfig.indexName && indexConfig.enabled && (
                <div className="px-3 pb-3 pt-0 border-t border-zinc-200 mt-0">
                  <div className="pt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {FUZZINESS_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        type="button"
                        onClick={() => handleIndexFuzzinessChange(indexConfig.indexName, preset.value)}
                        className={`p-2 rounded border text-left text-sm transition-all ${
                          indexConfig.fuzziness === preset.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                      >
                        <div className="font-medium text-zinc-900">{preset.label}</div>
                        <div className="text-xs text-zinc-500 truncate">{preset.description}</div>
                      </button>
                    ))}
                  </div>

                  {/* Custom input for index */}
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowCustomInput((prev) => ({ ...prev, [indexConfig.indexName]: !prev[indexConfig.indexName] }))}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {showCustomInput[indexConfig.indexName] ? 'Hide custom input' : 'Enter custom value...'}
                    </button>
                    {showCustomInput[indexConfig.indexName] && (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          placeholder="e.g., AUTO:3,6"
                          value={isCustomFuzziness(indexConfig.fuzziness) ? indexConfig.fuzziness : ''}
                          onChange={(e) => handleIndexFuzzinessChange(indexConfig.indexName, e.target.value)}
                          className="flex-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-4 border-t border-zinc-200">
        <button
          type="button"
          onClick={handleReset}
          className="px-4 py-2 text-sm font-medium text-zinc-700 bg-white border border-zinc-300 rounded-lg hover:bg-zinc-50"
        >
          Reset to Defaults
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
            hasChanges && !saving
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-zinc-200 text-zinc-500 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Last Updated */}
      {config.lastUpdated && (
        <div className="text-xs text-zinc-500 text-right">
          Last updated: {new Date(config.lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
}
